import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import Toast from 'components/ux/toast/Toast';

const API_BASE = process.env.REACT_APP_API_URL || '';

const Login = () => {
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const codeInputRef = useRef(null);

  const [bookers, setBookers] = useState([]);
  const [selectedBookerId, setSelectedBookerId] = useState('');
  const [step, setStep] = useState('select'); // 'select' | 'verify'
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const from = location.state?.from?.pathname || '/';

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  // Fetch booker list on mount
  useEffect(() => {
    fetch(`${API_BASE}/api/auth/bookers`)
      .then((res) => res.json())
      .then((data) => setBookers(data.bookers || []))
      .catch(() => setError('Failed to load booker list'));
  }, []);

  // Focus code input when step changes to verify
  useEffect(() => {
    if (step === 'verify' && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [step]);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError('');

    if (!selectedBookerId) {
      setError('Please select your name');
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/auth/request-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bookerId: selectedBookerId }),
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to send code');
      }

      setStep('verify');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');

    if (!code.trim()) {
      setError('Please enter the 6-digit code');
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/auth/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bookerId: selectedBookerId, code: code.trim() }),
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Verification failed');
      }

      login(data.token, data.booker);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectedBooker = bookers.find((b) => b.id === selectedBookerId);

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img
            src="/images/logo-dark.png"
            alt="Kirsebærkroen"
            className="h-14 mx-auto mb-4"
          />
          <h1 className="text-xl font-medium text-brand-dark">Agent Portal</h1>
          <p className="text-sm text-brand-muted mt-1">Sign in to continue</p>
        </div>

        <div className="card p-6">
          {error && (
            <div className="mb-4">
              <Toast message={error} type="error" dismissError={() => setError('')} />
            </div>
          )}

          {step === 'select' ? (
            <form onSubmit={handleRequestCode}>
              <label className="block text-sm font-medium text-brand-dark mb-2">
                Select your name
              </label>
              <select
                value={selectedBookerId}
                onChange={(e) => setSelectedBookerId(e.target.value)}
                className="input-field mb-4"
              >
                <option value="">Choose...</option>
                {bookers.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>

              {selectedBookerId && (
                <p className="text-xs text-brand-muted mb-4">
                  A login code will be sent to your email
                </p>
              )}

              <button
                type="submit"
                disabled={loading || !selectedBookerId}
                className={`btn-gold w-full ${loading || !selectedBookerId ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                    Sending...
                  </span>
                ) : (
                  'Send login code'
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyCode}>
              <p className="text-sm text-brand-muted mb-4">
                Code sent to <strong>{selectedBooker?.name || 'your email'}</strong>
              </p>

              <label className="block text-sm font-medium text-brand-dark mb-2">
                Enter 6-digit code
              </label>
              <input
                ref={codeInputRef}
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="input-field font-mono text-center text-2xl tracking-[0.3em] mb-4"
                autoComplete="one-time-code"
              />

              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className={`btn-gold w-full ${loading || code.length !== 6 ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                    Verifying...
                  </span>
                ) : (
                  'Sign in'
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setStep('select');
                  setCode('');
                  setError('');
                }}
                className="block w-full text-center text-sm text-brand-muted hover:text-brand-dark mt-3 transition-colors"
              >
                Back
              </button>
            </form>
          )}
        </div>

        <div className="flex items-center justify-center gap-2 mt-6">
          <img src="/images/nordic-logo.webp" alt="Nordic" className="h-6 w-auto opacity-50" />
          <span className="text-xs text-brand-muted">Nordic Info BV</span>
        </div>
      </div>
    </div>
  );
};

export default Login;
